import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Admins from '../Admins/Admins';
import Orders from '../Orders/Orders';
import Drivers from '../Drivers/Drivers';
import DispatchPlanner from '../Pages/DispatchPlanner/DispatchPlanner';
import DispatchHistory from '../Pages/DispatchHistory/DispatchHistory';
import Settings from '../Settings/Settings';
import Welcome from '../Welcome/Welcome';
import Products from '../Products/Products';
import EventLogs from '../EventLogs/EventLogs';
import Reports from '../Reports/Reports';
import Banners from '../Homepage/Banners/Banners';
import ReelVideos from '../Homepage/ReelVideos/ReelVideos';
import Analytics from '../Pages/Analytics/Analytics';
import './Dashboard.css';
import { hasStoreFeature } from '../../constants/stores';

const Dashboard = ({ onLogout, adminData, setAdminData }) => {
  const [currentPage, setCurrentPage] = useState('welcome');
  const [ordersNavState, setOrdersNavState] = useState(null);
  const [reportsNavState, setReportsNavState] = useState(null);
  const [productsNavState, setProductsNavState] = useState(null);
  const [eventLogsFilter, setEventLogsFilter] = useState(null);
  const [dispatchHasUnsavedChanges, setDispatchHasUnsavedChanges] = useState(false);
  const [showUnsavedNavigationModal, setShowUnsavedNavigationModal] = useState(false);
  const [pendingPage, setPendingPage] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onDispatchPage = ['dispatch', 'dispatch-planner', 'dispatch-history'].includes(currentPage);
    if (onDispatchPage && !hasStoreFeature(adminData, 'dispatch')) {
      setCurrentPage('welcome');
      navigate('/dashboard', { state: { page: 'welcome' }, replace: true });
    }
  }, [adminData, currentPage, navigate]);

  useEffect(() => {
    if (location.state?.page) {
      setCurrentPage(location.state.page);
      if (location.state.page === 'orders') {
        setOrdersNavState(location.state.productFilter
          ? { productFilter: location.state.productFilter }
          : null);
      }
      if (location.state.page === 'reports') {
        setReportsNavState(location.state.productFilter
          ? { productFilter: location.state.productFilter }
          : null);
      }
      if (location.state.page === 'products') {
        setProductsNavState(location.state.openProductId
          ? { openProductId: location.state.openProductId }
          : null);
      }
      if (location.state.page === 'event-logs') {
        setEventLogsFilter(location.state.eventLogFilter || null);
      }
    }
  }, [location.state]);

  const handlePageChange = (page) => {
    if (currentPage === 'dispatch-planner' && dispatchHasUnsavedChanges && page !== 'dispatch-planner') {
      setPendingPage(page);
      setShowUnsavedNavigationModal(true);
      return;
    }

    navigate('/dashboard', { state: { page } });
    setCurrentPage(page);
    if (page !== 'orders') {
      setOrdersNavState(null);
    }
    if (page !== 'reports') {
      setReportsNavState(null);
    }
    if (page !== 'products') {
      setProductsNavState(null);
    }
    if (page !== 'event-logs') {
      setEventLogsFilter(null);
    }
  };

  const confirmLeaveDispatch = () => {
    setShowUnsavedNavigationModal(false);
    setDispatchHasUnsavedChanges(false);
    if (pendingPage) {
      navigate('/dashboard', { state: { page: pendingPage } });
      setCurrentPage(pendingPage);
      setPendingPage(null);
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'admins':
        return <Admins adminData={adminData} setAdminData={setAdminData} />;
      case 'orders':
        return (
          <Orders
            adminData={adminData}
            productFilter={ordersNavState?.productFilter || null}
            onClearProductFilter={() => {
              setOrdersNavState(null);
              navigate('/dashboard', { state: { page: 'orders' }, replace: true });
            }}
          />
        );
      case 'drivers':
        return <Drivers adminData={adminData} />;
      case 'dispatch':
      case 'dispatch-planner':
        return (
          <DispatchPlanner
            adminData={adminData}
            onUnsavedChange={setDispatchHasUnsavedChanges}
            onPageChange={handlePageChange}
          />
        );
      case 'dispatch-history':
        return <DispatchHistory onPageChange={handlePageChange} adminData={adminData} />;
      case 'products':
        return (
          <Products
            adminData={adminData}
            openProductId={productsNavState?.openProductId || null}
            onOpenProductConsumed={() => {
              setProductsNavState(null);
              navigate('/dashboard', { state: { page: 'products' }, replace: true });
            }}
          />
        );
      case 'homepage-banners':
        return <Banners adminData={adminData} />;
      case 'homepage-reels':
        return <ReelVideos adminData={adminData} />;
      case 'reports':
        return (
          <Reports
            adminData={adminData}
            productFilter={reportsNavState?.productFilter || null}
            onClearProductFilter={() => {
              setReportsNavState(null);
              navigate('/dashboard', { state: { page: 'reports' }, replace: true });
            }}
          />
        );
      case 'analytics':
        return <Analytics />;
      case 'settings':
        return <Settings adminData={adminData} />;
      case 'event-logs':
        return <EventLogs adminData={adminData} incomingFilter={eventLogsFilter} />;
      case 'welcome':
      default:
        return <Welcome adminData={adminData} />;
    }
  };

  return (
    <div className="dashboard">
      <Sidebar
        onLogout={onLogout}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        adminData={adminData}
        setAdminData={setAdminData}
      />
      <main className="dashboard-main">
        <div className={`dashboard-content ${(currentPage === 'dispatch-planner' || currentPage === 'dispatch') ? 'dashboard-content--dispatch' : ''}`}>
          {renderPage()}
        </div>
      </main>

      {showUnsavedNavigationModal && (
        <div className="dispatch-unsaved-nav-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ marginTop: 0 }}>Unsaved route changes</h3>
            <p style={{ color: '#64748b' }}>
              You have unsaved dispatch changes. Leave without saving?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowUnsavedNavigationModal(false); setPendingPage(null); }}>
                Stay
              </button>
              <button type="button" onClick={confirmLeaveDispatch} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8 }}>
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
